/**
 * SocketContext - WebSocket connection management for real-time updates
 * 
 * This context provides a Socket.IO connection to the backend for receiving
 * real-time inbox and chat updates without polling.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '../utils/config';
import { useAuth } from '../context/AuthContext';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    joinInbox: (inboxId: string) => void;
    leaveInbox: (inboxId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    joinInbox: () => { },
    leaveInbox: () => { },
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
    children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const currentInboxRef = useRef<string | null>(null);
    // Follow the live session token, not a one-time localStorage read: the
    // handshake carries the JWT, so a session that re-authenticates while the
    // tab is open used to keep dialling with the stale (rejected) token until
    // a full page reload.
    const { token: authToken } = useAuth();
    // Same string on mount (saved token) and after AuthContext hydrates, so
    // the effect below does NOT tear the socket down and redial on load.
    const token = authToken || localStorage.getItem('auth_token');

    useEffect(() => {
        if (!token) {
            return;
        }

        // Connect to WebSocket server
        // Extract base host and path prefix for Socket.IO
        // API_BASE_URL = https://rfq2-api.gordiananalytics.com/rfq/api
        // We need host = https://rfq2-api.gordiananalytics.com, path = /rfq/socket.io
        const apiUrl = config.API_BASE_URL;
        const url = new URL(apiUrl);
        const pathPrefix = url.pathname.replace(/\/api\/?$/, '');  // "/rfq"
        const socketHost = url.origin;  // "https://rfq2-api.gordiananalytics.com"
        const socketPath = `${pathPrefix}/socket.io`;  // "/rfq/socket.io"

        const newSocket = io(socketHost, {
            path: socketPath,
            transports: ['websocket', 'polling'],
            query: { token },
            autoConnect: true,
            reconnection: true,
            // NEVER stop trying (owner 2026-08-26). With 5 attempts a laptop
            // that slept, a wifi blip or a backend restart permanently killed
            // the socket: from then on every `inbox_updated` was lost and open
            // panels only refreshed on their fallback poll — the "sometimes
            // the page doesn't update, totally at random" report. Backoff caps
            // at 10s so a long outage costs at most one attempt per 10s.
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
        });

        newSocket.on('connect', () => {
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('🔌 Socket: Connection error -', error.message);
        });

        newSocket.on('connected', (data) => {
        });

        setSocket(newSocket);

        return () => {
            newSocket.close();
        };
    }, [token]);

    const joinInbox = useCallback((inboxId: string) => {
        if (socket && isConnected) {
            // Leave previous inbox room if any
            if (currentInboxRef.current && currentInboxRef.current !== inboxId) {
                socket.emit('leave_inbox', { inbox_id: currentInboxRef.current });
            }

            // Join new inbox room
            socket.emit('join_inbox', { inbox_id: inboxId });
            currentInboxRef.current = inboxId;
        }
    }, [socket, isConnected]);

    const leaveInbox = useCallback((inboxId: string) => {
        if (socket && isConnected) {
            socket.emit('leave_inbox', { inbox_id: inboxId });
            if (currentInboxRef.current === inboxId) {
                currentInboxRef.current = null;
            }
        }
    }, [socket, isConnected]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, joinInbox, leaveInbox }}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
